import { Loader2 } from 'lucide-react'
import qs from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { completeRegistration } from '../../api/registrationApi'
import { applySignUpSession } from './signUpSession'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { ErrorBox, InfoBox, RegistrationShell, linkButtonClass, primaryButtonClass } from './RegistrationShell'

/**
 * /register/complete?token=…&session_id=… — Stripe sends the browser here after Checkout. The
 * backend verifies the session, provisions the calibration center + owner (idempotently; the
 * webhook may already have) and returns the same auth payload /authenticate would. We apply
 * it exactly as AuthForm does and land the new owner on Home.
 */
export const RegisterComplete = () => {
  const history = useHistory()
  const [error, setError] = useState(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const { token, session_id: sessionId } = qs.parse(window.location.search) || {}
    if (!token || !sessionId) {
      setError({ status: 404, message: 'This link is not valid.' })
      return
    }

    const run = async () => {
      try {
        const result = await completeRegistration(token, sessionId)
        await applySignUpSession(result?.auth, result?.email)
        history.replace('/')
      } catch (err) {
        setError({ status: err?.response?.status, message: getFriendlyErrorMessage(err) })
      }
    }
    run()
  }, [history])

  if (error) {
    return (
      <RegistrationShell width='narrow' title='We couldn’t finish setting up'>
        <ErrorBox>{error.message}</ErrorBox>
        <InfoBox>
          If your payment went through, your account may already be ready — try signing in. Otherwise contact support and we&apos;ll sort it
          out.
        </InfoBox>
        <button type='button' onClick={() => history.push('/authenticate')} className={`${primaryButtonClass} mt-5`}>
          Sign in
        </button>
        <div className='mt-3 text-center'>
          <button type='button' onClick={() => window.location.reload()} className={linkButtonClass}>
            Try again
          </button>
        </div>
      </RegistrationShell>
    )
  }

  return (
    <RegistrationShell width='narrow' title='Setting up your account…'>
      <div className='flex flex-col items-center gap-3 py-6' aria-busy='true'>
        <Loader2 className='h-6 w-6 animate-spin text-white/70' />
        <span className='text-[13px] text-white/60'>Creating your calibration center and setting up your plan.</span>
      </div>
    </RegistrationShell>
  )
}
